import { LoaderCircle, MailQuestion } from "lucide-react";
import { Navigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { authClient } from "../../lib/auth-client";
import { platformLastOrganizationKey, usePlatformAuth } from "../../lib/use-platform-auth";

const logoDark = "/branding/logo-dark.svg";

export function RootRedirectPage() {
  const { sessionData, isPending, bootstrapLoading, profile, profileSetupGateReady, needsProfileSetup, defaultOrganization, organizations } =
    usePlatformAuth();

  if (isPending || (sessionData?.user && (!profileSetupGateReady || (bootstrapLoading && !profile)))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sessionData?.user) {
    return <Navigate to="/auth/login" replace />;
  }

  if (needsProfileSetup) {
    return <Navigate to="/auth/profile-setup" replace />;
  }

  const rememberedOrganizationSlug =
    sessionData?.user?.id && typeof window !== "undefined"
      ? window.sessionStorage.getItem(platformLastOrganizationKey(sessionData.user.id))
      : null;
  const rememberedOrganization = rememberedOrganizationSlug
    ? organizations.find((organization) => organization.slug === rememberedOrganizationSlug) ?? null
    : null;
  const preferredOrganization = profile?.defaultOrganizationId
    ? organizations.find((organization) => organization.id === profile.defaultOrganizationId) ?? null
    : null;
  const redirectOrganization = rememberedOrganization ?? preferredOrganization ?? defaultOrganization;

  if (redirectOrganization) {
    return <Navigate to={`/${redirectOrganization.slug}`} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="glass w-full max-w-xl rounded-[2rem] border border-white/60 p-8 text-center sm:p-10">
        <img src={logoDark} alt="Forhandlerbørsen" className="mx-auto h-12 w-auto sm:h-14" />

        <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-primary shadow-[var(--shadow-premium)]">
          <MailQuestion className="h-7 w-7" />
        </div>

        <p className="mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Ingen tilgang funnet
        </p>
        <h1 className="mt-3 text-4xl font-black text-primary">Denne e-posten har ingen aktiv organisasjon</h1>
        <p className="mt-4 text-base leading-7 text-muted-foreground">
          Prøv med en annen e-post hvis du har flere brukere hos Forhandlerbørsen.
        </p>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Hvis du mener dette er feil, kontakt <a className="font-semibold text-primary underline decoration-accent/40 underline-offset-4" href="mailto:support@forhandlerborsen.no">support@forhandlerborsen.no</a>.
        </p>

        <div className="mt-8 flex justify-center">
          <Button
            type="button"
            className="w-full sm:w-auto"
            onClick={() => authClient.signOut()}
          >
            Prøv med en annen e-post
          </Button>
        </div>
      </div>
    </div>
  );
}
